import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { store } from '../../../lib/store'
import { lineDeleteRequest } from '../../../action/line-actions'
import { openShapePopup } from '../buildShapePopupContent'
import { normalizeTrackWidth } from '../../../lib/trackColor'

const SRC = 'bm-lines-src'
// One layer per line style (line-dasharray isn't data-driven); color + width stay
// data-driven. Fixed at 3 layers regardless of line count.
const LAYERS = { solid: 'bm-lines-solid', dashed: 'bm-lines-dashed', dots: 'bm-lines-dots' }
const LAYER_IDS = [LAYERS.solid, LAYERS.dashed, LAYERS.dots]

function parseStrokeColor(rawColor) {
  if (!rawColor) return '#0009F9'
  if (rawColor.length === 9) return '#' + rawColor.substring(3, 9)
  return rawColor.startsWith('#') ? rawColor : '#' + rawColor
}

function buildFC(lines) {
  const features = []
  for (const line of lines) {
    let geoJson
    try { geoJson = line.geoJson && JSON.parse(line.geoJson) } catch { continue }
    if (!geoJson) continue
    const feat = geoJson.features?.[0]
    if (!feat) continue
    const rawCoords = feat.geometry?.coordinates
    if (!Array.isArray(rawCoords) || rawCoords.length < 2) continue
    const lineProps = feat.properties?.line || {}
    const style = lineProps.style === 'dashed' || lineProps.style === 'dots' ? lineProps.style : 'solid'
    features.push({
      ...feat,
      properties: {
        ...feat.properties,
        _id: line.id,
        _strokeColor: parseStrokeColor(lineProps.color ?? feat.properties?.color),
        _style: style,
        _width: normalizeTrackWidth(lineProps.width)
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

function teardown(m) {
  if (!m) return
  LAYER_IDS.forEach(id => { if (m.getLayer(id)) m.removeLayer(id) })
  if (m.getSource(SRC)) m.removeSource(SRC)
}

const RenderLines = ({ map, lines }) => {
  const activeMapRef = useRef(null)

  useEffect(() => {
    if (!map) return

    if (activeMapRef.current && activeMapRef.current !== map) {
      teardown(activeMapRef.current)
    }
    activeMapRef.current = map

    // Extracted so it can run both now and again on style.load. Shapes used to be
    // redrawn only by the styleKey remount, which waits for idle — i.e. for every
    // tile to settle — so a slow layer server delayed them by seconds.
    const apply = () => {
      const fc = buildFC(lines)

      if (map.getSource(SRC)) {
        map.getSource(SRC).setData(fc)
        return
      }

      map.addSource(SRC, { type: 'geojson', data: fc })

      const basePaint = {
        'line-color': ['get', '_strokeColor'],
        'line-width': ['get', '_width'],
        'line-opacity': 0.9
      }
      map.addLayer({
        id: LAYERS.solid, type: 'line', source: SRC,
        filter: ['==', ['get', '_style'], 'solid'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: basePaint
      })
      map.addLayer({
        id: LAYERS.dashed, type: 'line', source: SRC,
        filter: ['==', ['get', '_style'], 'dashed'],
        layout: { 'line-join': 'round', 'line-cap': 'butt' },
        paint: { ...basePaint, 'line-dasharray': [2, 2] }
      })
      map.addLayer({
        id: LAYERS.dots, type: 'line', source: SRC,
        filter: ['==', ['get', '_style'], 'dots'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { ...basePaint, 'line-dasharray': [0, 2] }
      })

      const clickHandler = (e) => {
        const feat = e.features?.[0]
        if (!feat) return
        const id = feat.properties._id
        const line = store.getState().lines.find(l => l.id === id)
        if (!line) return
        let popup
        const onDelete = () => {
          popup?.remove()
          store.dispatch(lineDeleteRequest(id))
        }
        popup = openShapePopup(map, line, 'line', e.lngLat, onDelete)
      }
      const enterHandler = () => { map.getCanvas().style.cursor = 'pointer' }
      const leaveHandler = () => { map.getCanvas().style.cursor = '' }

      LAYER_IDS.forEach(id => {
        map.on('click', id, clickHandler)
        map.on('mouseenter', id, enterHandler)
        map.on('mouseleave', id, leaveHandler)
      })
    }

    apply()
    // Re-add on style.load, which fires as soon as the new style is parsed —
    // addSource only needs that, not the fully-loaded style isStyleLoaded() waits
    // for. The styleKey remount still happens later at idle; apply is idempotent
    // (setData when the source already exists) so running twice is harmless.
    map.on('style.load', apply)
    return () => map.off('style.load', apply)
  }, [map, lines])

  useEffect(() => {
    return () => teardown(activeMapRef.current)
  }, [])

  return null
}

const mapStateToProps = state => ({ lines: state.lines })
export default connect(mapStateToProps)(RenderLines)
